import { useCallback, useEffect, useRef, useState } from 'react';

import { useSkydioImportStatusLazyQuery } from 'types/atlas-graphql';
import type { SkydioImportStatus } from 'utils/skydio';

const pollIntervalMs = 5000;
const requestTimeoutMs = 15000;
const maxConsecutiveFailures = 4;

export function useSkydioImportStatusPolling(recordId?: string) {
  const [loadStatus] = useSkydioImportStatusLazyQuery({ fetchPolicy: 'network-only' });
  const loadStatusRef = useRef(loadStatus);
  loadStatusRef.current = loadStatus;
  const [status, setStatus] = useState<SkydioImportStatus>();
  const [error, setError] = useState<Error>();
  const [retryGeneration, setRetryGeneration] = useState(0);
  const retry = useCallback(() => setRetryGeneration((generation) => generation + 1), []);

  useEffect(() => {
    setStatus(undefined);
    setError(undefined);
    if (!recordId) {
      return;
    }

    let active = true;
    let consecutiveFailures = 0;
    let timer: ReturnType<typeof setTimeout>;
    let requestTimer: ReturnType<typeof setTimeout>;
    let controller: AbortController | undefined;
    const poll = async () => {
      controller = new AbortController();
      requestTimer = setTimeout(() => controller?.abort(), requestTimeoutMs);
      try {
        const result = await loadStatusRef.current({
          variables: { recordId },
          context: { fetchOptions: { signal: controller.signal } },
        });
        if (!active) {
          return;
        }
        if (!result.data?.skydioImportStatus) {
          throw new Error('Skydio import status returned no data');
        }
        const next = result.data.skydioImportStatus;
        consecutiveFailures = 0;
        setError(undefined);
        setStatus(next);
        if (!['COMPLETE', 'FAILED'].includes(next.status)) {
          timer = setTimeout(poll, pollIntervalMs);
        }
      } catch (caught) {
        if (!active) {
          return;
        }
        consecutiveFailures += 1;
        if (consecutiveFailures >= maxConsecutiveFailures) {
          setError(caught instanceof Error ? caught : new Error(String(caught)));
          return;
        }
        timer = setTimeout(poll, pollIntervalMs * 2 ** (consecutiveFailures - 1));
      } finally {
        clearTimeout(requestTimer);
      }
    };

    void poll();
    return () => {
      active = false;
      clearTimeout(timer);
      clearTimeout(requestTimer);
      controller?.abort();
    };
  }, [recordId, retryGeneration]);

  return { error, retry, status };
}
